import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, FormEvent } from 'react';
import { auth } from '../api';
import { useAuth, activeMembership, canManageMembers } from '../stores/auth';

export function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    email, name, memberships, activeOrgId, isPlatformAdmin, logout, setAuth, switchOrg,
  } = useAuth();
  const [showPwModal, setShowPwModal] = useState(false);
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);

  // Refresh memberships on mount
  useEffect(() => {
    auth
      .me()
      .then((r) => setAuth({
        email: r.user.email,
        name: r.user.name,
        isPlatformAdmin: r.user.is_platform_admin,
        memberships: r.memberships,
      }))
      .catch(() => { /* 401 -> logout via interceptor */ });
  }, [setAuth]);

  const isPlatformView = location.pathname.startsWith('/platform');
  const activeOrg = activeMembership();
  const activeOrgName =
    activeOrg?.org_name ??
    memberships.find((m) => m.org_id === activeOrgId)?.org_name ??
    (isPlatformAdmin ? 'Platform Admin' : '—');

  function doLogout() {
    logout();
    navigate('/login');
  }

  function pickOrg(orgId: string) {
    switchOrg(orgId);
    setShowOrgSwitcher(false);
    if (location.pathname.startsWith('/platform')) navigate('/devices');
  }

  return (
    <div className="app">
      <aside className="sidebar">
        {/* Org switcher */}
        <div className="org-switcher" onClick={() => setShowOrgSwitcher(!showOrgSwitcher)}>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ fontSize: '0.75em', color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {isPlatformView ? 'Viewing' : 'Org'}
            </div>
            <div style={{ fontWeight: 600, fontSize: '0.95em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isPlatformView ? 'Platform Admin' : activeOrgName}
            </div>
          </div>
          <div style={{ color: 'var(--fg-3)' }}>▾</div>
        </div>

        {showOrgSwitcher && (
          <div className="org-dropdown">
            {memberships.map((m) => (
              <div key={m.org_id}
                className={'org-dropdown-item' + (m.org_id === activeOrgId && !isPlatformView ? ' selected' : '')}
                onClick={() => pickOrg(m.org_id)}>
                <div style={{ fontWeight: 500 }}>{m.org_name}</div>
                <div style={{ fontSize: '0.78em', color: 'var(--fg-3)' }}>{m.org_slug} · {m.role}</div>
              </div>
            ))}
            {memberships.length === 0 && (
              <div style={{ padding: '0.6em 1em', color: 'var(--fg-3)', fontSize: '0.85em' }}>
                No org memberships.
              </div>
            )}
            {isPlatformAdmin && (
              <>
                <div style={{ borderTop: '1px solid var(--border)', margin: '0.4em 0' }} />
                <div
                  className={'org-dropdown-item' + (isPlatformView ? ' selected' : '')}
                  onClick={() => { setShowOrgSwitcher(false); navigate('/platform/orgs'); }}
                  style={{ color: 'var(--accent)' }}
                >
                  <div style={{ fontWeight: 500 }}>⚙ Platform Admin</div>
                  <div style={{ fontSize: '0.78em', color: 'var(--fg-3)' }}>Manage all orgs and users</div>
                </div>
              </>
            )}
          </div>
        )}

        <nav>
          {isPlatformView ? (
            <>
              <NavLink to="/platform/orgs" className={({ isActive }) => isActive ? 'active' : ''}>Orgs</NavLink>
              <NavLink to="/platform/users" className={({ isActive }) => isActive ? 'active' : ''}>Users</NavLink>
              <NavLink to="/platform/monitoring" className={({ isActive }) => isActive ? 'active' : ''}>Monitoring</NavLink>
              <NavLink to="/platform/stats" className={({ isActive }) => isActive ? 'active' : ''}>Stats</NavLink>
            </>
          ) : activeOrgId ? (
            <>
              <NavLink to="/devices" className={({ isActive }) => isActive ? 'active' : ''}>Devices</NavLink>
              <NavLink to="/packages" className={({ isActive }) => isActive ? 'active' : ''}>Packages</NavLink>
              <NavLink to="/assets" className={({ isActive }) => isActive ? 'active' : ''}>Assets</NavLink>
              <NavLink to="/playlists" className={({ isActive }) => isActive ? 'active' : ''}>Playlists</NavLink>
              <NavLink to="/schedules" className={({ isActive }) => isActive ? 'active' : ''}>Schedules</NavLink>
              {canManageMembers() && (
                <>
                  {/* Phase 3e.1 — alarms; admin+ only */}
                  <NavLink to="/alarms" className={({ isActive }) => isActive ? 'active' : ''}>Alarms</NavLink>
                  {/* Phase 3d — idle banner; admin+ only */}
                  <NavLink to="/banner" className={({ isActive }) => isActive ? 'active' : ''}>Idle Banner</NavLink>
                  <NavLink to="/agent-releases" className={({ isActive }) => isActive ? 'active' : ''}>Agent releases</NavLink>
                  <NavLink to="/members" className={({ isActive }) => isActive ? 'active' : ''}>Members</NavLink>
                </>
              )}
            </>
          ) : (
            <div style={{ padding: '0.8em 1.4em', color: 'var(--fg-3)', fontSize: '0.88em' }}>
              Select an org from the dropdown.
            </div>
          )}
        </nav>

        <div className="userbox">
          <div style={{ color: 'var(--fg-2)', fontWeight: 500 }}>{name || email}</div>
          <div style={{ color: 'var(--fg-3)', fontSize: '0.78em', marginTop: '0.2em' }}>
            {email}
            {isPlatformAdmin && <span style={{ color: 'var(--accent)', marginLeft: '0.4em' }}>· platform admin</span>}
          </div>
          <button onClick={() => setShowPwModal(true)} style={{ marginTop: '0.7em', width: '100%' }}>
            Change password
          </button>
          <button onClick={doLogout} style={{ marginTop: '0.4em', width: '100%' }}>Sign out</button>
        </div>
      </aside>
      <main className="main"><Outlet key={activeOrgId || 'platform'} /></main>
      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
    </div>
  );
}

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (newPassword !== confirm) { setErr('New passwords do not match.'); return; }
    setBusy(true);
    try {
      await auth.changePassword({ currentPassword, newPassword });
      setOk(true);
      setTimeout(onClose, 1200);
    } catch (e: unknown) {
      const detail = (e as { detail?: { error?: string } }).detail?.error;
      setErr(detail === 'invalid_current_password' ? 'Current password is wrong.' : 'Could not change password.');
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} style={{ minWidth: 420 }}>
        <h2 style={{ marginTop: 0 }}>Change password</h2>
        {ok ? (
          <div style={{ color: 'var(--accent-2)' }}>Password updated.</div>
        ) : (
          <>
            <div className="field"><label>Current password</label>
              <input type="password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} required autoFocus /></div>
            <div className="field"><label>New password (min 10 chars)</label>
              <input type="password" value={newPassword} minLength={10} onChange={(e) => setNew(e.target.value)} required /></div>
            <div className="field"><label>Confirm new password</label>
              <input type="password" value={confirm} minLength={10} onChange={(e) => setConfirm(e.target.value)} required /></div>
            {err && <div className="error">{err}</div>}
            <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose}>Cancel</button>
              <button className="primary" type="submit" disabled={busy}>{busy ? 'Saving...' : 'Update'}</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
